import React, { useEffect } from "react";
import { Card, Table, Container, Row, Col, Alert, Button, Form, InputGroup } from "react-bootstrap";
import { useSuperState } from "helpers/hooks";

const Pricing = props => {
    const [state, setState] = useSuperState({
        error: null,
        total_tests_taken: 1000,
        avg_test_duration_mins: 45,
        peak_concurrent_users: 100,
        minimum_capacity: 50,
        hourly_rate: 1.00, // Added hourly rate field with default $1
        estimated_cost: null
    });

    const { error, total_tests_taken, avg_test_duration_mins, peak_concurrent_users, minimum_capacity, hourly_rate, estimated_cost } = state;

    const estimateCost = e => {
        e.preventDefault();

        try {

            // Constants
            const idleRate = 0.02; // Idle instance rate ($/hour)
            const licensingRate = 4.00; // Licensing cost per user ($/month)
            const sessionsPerInstance = 4; // Multi-session fleet
            const hoursPerMonth = 720; // 24/7 operation for minimum capacity

            // Convert inputs to numbers
            const tests = parseInt(total_tests_taken, 10);
            const durationMins = parseInt(avg_test_duration_mins, 10);
            const peakUsers = parseInt(peak_concurrent_users, 10);
            const minCapacity = parseInt(minimum_capacity, 10);
            const hourlyRateValue = parseFloat(hourly_rate);

            // Validate inputs
            if (isNaN(tests) || isNaN(durationMins) || isNaN(peakUsers) || isNaN(minCapacity) || isNaN(hourlyRateValue) ||
                tests < 0 || durationMins < 0 || peakUsers < 0 || minCapacity < 0 || hourlyRateValue < 0) {
                setState({ error: "Please enter valid positive numbers for all fields.", estimated_cost: null });
                return;
            }
            if (minCapacity > peakUsers) {
                setState({ error: "Minimum capacity cannot exceed peak concurrent users.", estimated_cost: null });
                return;
            }

            // Calculate total streaming hours (session-hours)
            const avgDurationHours = durationMins / 60;
            const totalStreamingHours = tests * avgDurationHours;

            // Calculate instance hours (multi-session: 4 sessions per instance) - assume ~15% slop
            const activeInstanceHours = totalStreamingHours / (sessionsPerInstance * 0.85);

            // Calculate minimum instances to maintain capacity
            const minimumInstances = Math.ceil(minCapacity / sessionsPerInstance);

            // Calculate idle instance hours (fleet maintains minimum capacity 24/7)
            const totalPossibleInstanceHours = minimumInstances * hoursPerMonth;
            const idleInstanceHours = Math.max(0, totalPossibleInstanceHours - activeInstanceHours);

            // Calculate costs using the hourly rate from the form
            const streamingCost = activeInstanceHours * hourlyRateValue;
            const idleCost = idleInstanceHours * idleRate;
            const licensingCost = peakUsers * licensingRate;

            const totalCost = (streamingCost + idleCost + licensingCost).toFixed(2);

            setState({ error: null, estimated_cost: totalCost });
        } catch (err) {
            setState({ error: "Error calculating cost: " + err.message, estimated_cost: null });
        }
    };

    return (
        <Container>
            <h1>Monthly Pricing Calculator</h1>
                <hr/>
                {error && <Alert variant="warning" onClose={() => { setState({ error: null }); }}>{error}</Alert>}
                {!error &&
                <Row>
                    <Col xs={8}>
                        <Card>
                            <Card.Body>
                                <Form onSubmit={estimateCost}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Total Tests Taken</Form.Label>
                                        <InputGroup className="mb-3">
                                            <Form.Control
                                                type="number"
                                                value={total_tests_taken}
                                                onChange={e => setState({ total_tests_taken: e.target.value })}
                                                    />
                                            <InputGroup.Text>tests</InputGroup.Text>
                                        </InputGroup>
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Average Test Session Duration</Form.Label>
                                        <InputGroup className="mb-3">
                                            <Form.Control
                                                type="number"
                                                value={avg_test_duration_mins}
                                                onChange={e => setState({ avg_test_duration_mins: e.target.value })}
                                                    />
                                            <InputGroup.Text>mins</InputGroup.Text>
                                        </InputGroup>
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Peak Concurrent Users</Form.Label>
                                        <InputGroup className="mb-3">
                                            <Form.Control
                                                type="number"
                                                value={peak_concurrent_users}
                                                onChange={e => setState({ peak_concurrent_users: e.target.value })}
                                                    />
                                            <InputGroup.Text>users</InputGroup.Text>
                                        </InputGroup>
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Minimum Capacity</Form.Label>
                                        <InputGroup className="mb-3">
                                            <Form.Control
                                                type="number"
                                                value={minimum_capacity}
                                                onChange={e => setState({ minimum_capacity: e.target.value })}
                                                    />
                                            <InputGroup.Text>sessions</InputGroup.Text>
                                        </InputGroup>
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Hourly Rate per Running Instance</Form.Label>
                                        <InputGroup className="mb-3">
                                            <InputGroup.Text>$</InputGroup.Text>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                value={hourly_rate}
                                                onChange={e => setState({ hourly_rate: e.target.value })}
                                                    />
                                            <InputGroup.Text>/hr</InputGroup.Text>
                                        </InputGroup>
                                    </Form.Group>

                                    <Button variant="primary" type="submit">
                                        Estimate
                                    </Button>
                                </Form>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col xs={4}>
                        <Card>
                            <Card.Body>
                                <Card.Title>Cost Estimate</Card.Title>
                                <h1>{estimated_cost ? '$' + estimated_cost + '/mo' : '-'}</h1>
                                <hr/>
                                <div className="text-secondary">
                                    <small><b>Running instance rate:</b> ${parseFloat(hourly_rate).toFixed(2)}/hr</small>
                                    <br/>
                                    <small><b>Idle instance rate:</b> $.02/hr</small>
                                    <br/>
                                    <small><b>Windows licensing fee:</b> $4.00/unique user</small>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
                }
            </Container>
    );
};

export default Pricing;
